/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { forwardRef, useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

export interface SpinningBorderProps extends Omit<ComponentProps<'div'>, 'children'> {
  children: ReactNode
  /** Duration of the spin animation in seconds. Default: 2 */
  duration?: number
  /** When true, spins indefinitely. When false, runs for duration then fades out. Default: false */
  continuous?: boolean
  /** Tailwind rounding classes for the animated ring wrapper. Default: `rounded-full`. */
  roundedClassName?: string
}

export const SpinningBorder = forwardRef<HTMLDivElement, SpinningBorderProps>((props, forwardedRef) => {
  const { children, duration = 2, continuous = false, roundedClassName, className, ...rest } = props
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const spinAnimation = continuous
    ? `sf-spinning-border-spin ${duration}s linear infinite`
    : `sf-spinning-border-spin-fade ${duration}s ease-in-out 1 forwards`

  return (
    <div className={cn('relative', className)} ref={forwardedRef} {...rest}>
      {isMounted ? (
        <div
          className={cn(
            'pointer-events-none absolute -inset-[3px] z-0 overflow-hidden p-[3px]',
            roundedClassName ?? 'rounded-full'
          )}
          style={{
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        >
          <div
            className="absolute -top-1/2 -left-1/2 h-[200%] w-[200%]"
            style={{
              background:
                'conic-gradient(from 0deg at 50% 50%, transparent 0deg 250deg, #e53e3e 280deg, #c53030 320deg, transparent 360deg)',
              animation: spinAnimation,
            }}
          />
        </div>
      ) : null}
      <div className="relative z-1">{children}</div>
    </div>
  )
})

SpinningBorder.displayName = 'SpinningBorder'
